import CustomButton from 'Components/CustomButton'
import DeleteDialog from 'Components/DeleteDialog'
import HomeCard from 'Services/SalesManagement/Features/Home/Components/HomeCard'
import { SaveShortcutDialog } from 'Services/SalesManagement/Features/Shortcut/Components/SaveShortcutDialog'
import { Shortcut } from 'Services/SalesManagement/Features/Shortcut/Components/Shortcut'
import {
  deleteShortcut,
  fetchShortcuts,
  type ShortcutDetailShape,
  type ShortcutShape
} from 'Services/SalesManagement/Features/Shortcut/shortcutSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { Box, CardActions, CardContent, CardHeader } from '@mui/material'
import Typography from '@mui/material/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { type FC, useState } from 'react'
import { t } from 'ttag'

const ShortcutList: FC<{
  shortcuts: ShortcutShape[]
  isSuperuser: boolean
}> = ({ shortcuts, isSuperuser }) => {
  const dispatch = useAppDispatch()

  const { submitLoading, deleteLoading } = useTypedSelector(state => state.shortcut)

  const initialState: ShortcutDetailShape = {
    id: null,
    title_fi: '',
    link: '',
    description_fi: '',
    order: 0
  }

  const [selectedShortcut, setSelectedShortcut] = useState<ShortcutDetailShape>(initialState)

  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)

  const handleEditClick = (selectedShortcut: ShortcutDetailShape) => {
    setSelectedShortcut(selectedShortcut)
    setIsSaveDialogOpen(true)
  }

  const handleDeleteClick = (selectedShortcut: ShortcutDetailShape) => {
    setSelectedShortcut(selectedShortcut)
    setIsDeleteDialogOpen(true)
  }

  const handleDelete = () => {
    const params = {
      id: selectedShortcut.id as number
    }
    dispatch(deleteShortcut(params))
      .then(unwrapResult)
      .then(() => {
        SnackbarUtils.success(t`Shortcut successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting shortcut.`)
      })
      .finally(() => {
        setIsDeleteDialogOpen(false)
        dispatch(fetchShortcuts())
      })
  }

  return (
    <>
      <DeleteDialog
        open={isDeleteDialogOpen}
        handleClose={() => setIsDeleteDialogOpen(false)}
        handleSubmit={handleDelete}
        loading={deleteLoading === 'pending'}
        title={t`Delete shortcut`}
      >
        <Typography component="span" color="text.primary">
          {t`Are you sure you want to delete the shortcut?`}
        </Typography>
      </DeleteDialog>
      <SaveShortcutDialog
        data={selectedShortcut}
        open={isSaveDialogOpen}
        loading={submitLoading === 'pending'}
        handleClose={() => setIsSaveDialogOpen(false)}
      />

      <HomeCard>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', flexDirection: 'column' }}>
          <CardHeader title={t`Shortcuts`} />
          <CardContent>
            {shortcuts.length > 0 ? (
              <>
                {shortcuts.map(shortcut => (
                  <Shortcut
                    key={shortcut.id}
                    shortcut={shortcut}
                    handleEdit={() => handleEditClick(shortcut)}
                    handleDelete={() => handleDeleteClick(shortcut)}
                    canControl={isSuperuser}
                  />
                ))}
              </>
            ) : (
              <Box mb={2}>
                <Typography color="text.secondary">{t`No shortcuts added`}</Typography>
              </Box>
            )}
          </CardContent>
        </Box>
        <CardActions>
          {isSuperuser && (
            <CustomButton
              sx={{ mb: 1 }}
              onClick={() => handleEditClick(initialState)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new shortcut`}
            </CustomButton>
          )}
        </CardActions>
      </HomeCard>
    </>
  )
}

export { ShortcutList }
